import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Wallet,
    Clock,
    CheckCircle2,
    CircleDot,
    Landmark,
    AlertCircle,
    ArrowRight,
    Send,
    FileText,
} from "lucide-react";

const STATUS_META = {
    paid: {
        label: "Paid",
        style: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: CheckCircle2,
    },
    processing: {
        label: "Processing",
        style: "bg-navy-50 text-navy-700 border-navy-100",
        icon: CircleDot,
    },
    requested: {
        label: "Requested",
        style: "bg-gold-500/10 text-gold-700 border-gold-200",
        icon: Clock,
    },
    upcoming: {
        label: "Upcoming",
        style: "bg-gold-500/10 text-gold-700 border-gold-200",
        icon: Clock,
    },
    on_hold: {
        label: "On hold",
        style: "bg-red-50 text-red-700 border-red-200",
        icon: AlertCircle,
    },
};

function money(amountCents, currency) {
    if (amountCents == null || !currency) return "—";
    return `${currency.toUpperCase()} ${(amountCents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AffiliatePayments() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requesting, setRequesting] = useState(false);

    const load = () =>
        api
            .get("/affiliate/payments")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
    }, []);

    const items = data?.items || [];
    const summary = data?.summary || {};
    const currency = summary.currency || "hkd";
    const hasProcessing = items.some((i) => i.status === "processing");
    const hasRequested = items.some((i) => i.status === "requested");

    const requestPayout = async () => {
        setRequesting(true);
        try {
            await api.post("/affiliate/payments/request");
            toast.success(
                "Payout requested — the Perx finance team has been notified."
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRequesting(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Commissions"
                title="Payments"
                description="Every commission Perx has paid — or will pay — for referrals you've converted. Payouts run on the 5th of each month."
                testid="affiliate-payments-header"
                action={
                    <div className="flex gap-2 flex-wrap">
                        {hasProcessing && (
                            <Button
                                onClick={requestPayout}
                                disabled={requesting || !data?.payout_ready}
                                data-testid="aff-request-payout-btn"
                                className="rounded-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 px-6 font-semibold disabled:opacity-50"
                            >
                                <Send size={14} className="mr-1.5" />
                                {requesting ? "Requesting…" : "Request payout"}
                            </Button>
                        )}
                        <Link to="/affiliate/payout-details">
                            <Button
                                variant={hasProcessing ? "outline" : "default"}
                                data-testid="aff-payments-payout-btn"
                                className={
                                    hasProcessing
                                        ? "rounded-full h-11 border-navy-200 text-navy-700 px-6"
                                        : "rounded-full h-11 bg-navy-700 hover:bg-navy-800 text-cream px-6"
                                }
                            >
                                <Landmark size={14} className="mr-1.5" />
                                {data?.payout_ready ? "Update payout details" : "Add payout details"}
                                <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                {!loading && !data?.payout_ready && (
                    <div
                        data-testid="aff-payments-missing-payout"
                        className="rounded-2xl border border-gold-300 bg-gold-50 p-4 flex items-start gap-3"
                    >
                        <AlertCircle size={18} className="text-gold-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-navy-700 leading-relaxed flex-1">
                            <span className="font-semibold">
                                We can&apos;t send you money yet.
                            </span>{" "}
                            You&rsquo;ve earned commissions but haven&rsquo;t told us
                            where to send them.
                            <Link
                                to="/affiliate/payout-details"
                                className="ml-1 underline text-navy-700"
                            >
                                Add payout details now
                            </Link>{" "}
                            so your next payout on the 5th lands smoothly.
                        </div>
                    </div>
                )}

                {hasRequested && (
                    <div
                        data-testid="aff-payments-requested-banner"
                        className="rounded-2xl border border-navy-100 bg-navy-50 p-4 flex items-start gap-3"
                    >
                        <Clock size={18} className="text-navy-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-navy-700 leading-relaxed">
                            <span className="font-semibold">Payout requested.</span>{" "}
                            Perx finance has been notified and will process your
                            commission on the next payout cycle. You&rsquo;ll see the
                            row flip to <span className="font-mono text-emerald-700">Paid</span>{" "}
                            (with a receipt) as soon as it settles.
                        </div>
                    </div>
                )}

                <div className="grid sm:grid-cols-3 gap-5">
                    <SummaryTile
                        icon={Wallet}
                        label="Lifetime paid"
                        value={money(summary.lifetime_amount || 0, currency)}
                        blurb={`${summary.paid_count || 0} payouts`}
                        accent
                    />
                    <SummaryTile
                        icon={CheckCircle2}
                        label="Year to date"
                        value={money(summary.ytd_amount || 0, currency)}
                        blurb="Since Jan 1"
                    />
                    <SummaryTile
                        icon={Clock}
                        label="Next payout"
                        value={
                            summary.next_payout
                                ? money(summary.next_payout.amount, summary.next_payout.currency || currency)
                                : "—"
                        }
                        blurb={
                            summary.next_payout
                                ? `${summary.next_payout.period_label || ""} · ${summary.next_payout.conversions || 0} conversions`
                                : "No commissions in progress"
                        }
                    />
                </div>

                <div
                    data-testid="affiliate-payments-table"
                    className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden"
                >
                    <div className="px-6 py-4 border-b border-navy-100/70 text-sm text-navy-500">
                        Payout ledger — most recent period first.
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Period
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Converted referrals
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Amount
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Status
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Paid on
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Reference
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Receipt
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-10 text-navy-500">
                                        Loading payouts…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-14 text-navy-400">
                                        <Wallet size={28} className="mx-auto mb-2 text-navy-300" />
                                        <div className="font-semibold text-navy-700">
                                            No commissions yet.
                                        </div>
                                        <div className="text-xs mt-1">
                                            As soon as your referred friends buy a Perx
                                            membership, your first payout lands here.
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {items.map((r) => {
                                const meta = STATUS_META[r.status] || STATUS_META.upcoming;
                                const Icon = meta.icon;
                                return (
                                    <TableRow
                                        key={r.id}
                                        data-testid={`aff-payment-row-${r.id}`}
                                        className={r.status === "processing" ? "bg-gold-500/5" : ""}
                                    >
                                        <TableCell className="font-medium text-navy-700">
                                            {r.period_label || `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}`}
                                            <div className="text-[11px] text-navy-500 font-normal mt-0.5">
                                                {fmtDate(r.period_start)} – {fmtDate(r.period_end)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-navy-700 font-semibold">
                                            {r.conversions ?? 0}
                                        </TableCell>
                                        <TableCell className="text-navy-700 font-semibold">
                                            {money(r.amount, r.currency)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={`${meta.style} border uppercase tracking-widest text-[10px] gap-1`}
                                            >
                                                <Icon size={11} className="mr-0.5" />
                                                {meta.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-navy-500 text-sm">
                                            {r.paid_at ? fmtDate(r.paid_at) : "—"}
                                        </TableCell>
                                        <TableCell className="font-mono text-[11px] text-navy-500">
                                            {r.reference || "—"}
                                        </TableCell>
                                        <TableCell>
                                            {r.receipt_url ? (
                                                <a
                                                    href={r.receipt_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`aff-payment-receipt-${r.id}`}
                                                    className="inline-flex items-center gap-1 text-navy-700 hover:text-navy-800 text-xs underline underline-offset-2"
                                                >
                                                    <FileText size={12} />
                                                    View
                                                </a>
                                            ) : (
                                                <span className="text-navy-300 text-xs">—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}

function SummaryTile({ icon: Icon, label, value, blurb, accent }) {
    return (
        <div
            className={`rounded-2xl border p-5 ${
                accent
                    ? "bg-navy-700 text-cream border-navy-800"
                    : "bg-white text-navy-700 border-navy-100"
            }`}
        >
            <div
                className={`flex items-center gap-2 text-[11px] uppercase tracking-widest ${
                    accent ? "text-gold-400" : "text-navy-500"
                }`}
            >
                <Icon size={14} />
                {label}
            </div>
            <div className={`font-display text-3xl mt-2 ${accent ? "text-cream" : "text-navy-700"}`}>
                {value}
            </div>
            <div className={`text-[11px] mt-1 ${accent ? "text-cream/70" : "text-navy-500"}`}>
                {blurb}
            </div>
        </div>
    );
}
